import ApiContainer from '@/orm/ApiContainer'
import { i18n, setLocaleAsync } from '@/i18n'
import { throttle } from 'lodash'
import Moment from 'moment'
import { subscribeToCustomerRooms, unsubscribeFromRemovedCustomerRooms } from '@/util/customerSocketRooms'

const throttleSettings = {
  leading: false,
  trailing: true
}

// 500 milliseconds
const throttleSpeed = 500

/**
 * Fetches an up-to-date version of the user record for the current user.
 * Wrapped in throttle to ensure we don't ask for the new user more than once
 * ever 500ms (at the moment our back-end is very chatty and will fire off
 * several update events in a few 100ms)
 *
 * @return undefined
 */
export const throttledUpdateUser = throttle((context, message) => {
  if (context.getters.ignoreNextUserUpdate) {
    context.commit('setIgnoreNextUserUpdate', false)
    return
  }
  ApiContainer.getMapper('User')
    .find('self', {
      params: {
        expand: 'customers,roles,role_permissions,user_settings',
        fields: '*,role_permissions.scope,role_permissions.permission_code,user_settings.key,user_settings.value,customers.id,customers.name,customers.parent_id,roles.id,roles.code,roles.name'
      }
    })
    .then((user) => {
      context.commit('setUser', user)
    })
}, throttleSpeed, throttleSettings)

/**
 * Clears existing translations for all locales and fetches the latest
 * translations for the current locale.
 *
 * @return undefined
 */
const throttledUpdateLocale = throttle((context, message) => {
  context.dispatch('localeModule/clearTranslationStrings', context.rootGetters['localeModule/locale'], { root: true })
  setLocaleAsync(context.rootGetters['localeModule/locale'], true)
}, throttleSpeed, throttleSettings)

/**
 * Fetches an up-to-date version of the customer record for the current customer
 * context.
 *
 * @return undefined
 */
export const throttledUpdateCustomer = throttle((context, message) => {
  ApiContainer.getMapper('Customer')
    .find('self', { params: { expand: 'customer_settings' } })
    .then((customer) => {
      context.commit('setCustomerContext', customer)
    })
}, throttleSpeed, throttleSettings)

const updateMaintenanceMode = (context, message) => {
  // If maintenenceMode is transitioning to off, show a message to that effect
  if (message.maintenanceMode === false && context.rootGetters['globalMessagingModule/maintenanceMode'] === true) {
    context.dispatch('globalMessagingModule/showSuccessFlashBanner', i18n.t('maintenance_completed_message'), { root: true })
  }
  context.dispatch('globalMessagingModule/setMaintenanceMode', message.maintenanceMode, { root: true })
}

export default {
  state: {
    /**
     * @var UserRecord
     */
    user: ApiContainer.createRecord('User', {}),

    /**
     * @var Moment
     */
    userCacheAge: Moment().subtract(2, 'days'),

    /**
     * @var CustomerRecord
     */
    customerContext: ApiContainer.createRecord('Customer', {}),

    /**
     * Used to house the customer context id temporarily when switching from one
     * customer context to another
     *
     * @var Integer
     */
    interimCustomerContext: null,

    /**
     * Whether the user should stay logged in across sessions.
     * @var Boolean
     */
    keepLoggedIn: false,

    /**
     * The url to return to after logging in
     * @var String
     */
    returnUrl: '',

    /**
     * Unixtime
     *
     * @var Integer
     */
    sessionExpirationTime: 0,

    /**
     * Whether the next user update event pushed via socketio will be ignored.
     * This is useful when we've updated the user's password and don't want to
     * automatically make a user/self request with a now-invalid JWT.
     *
     * @var boolean
     */
    ignoreNextUserUpdate: false,

    /**
     * A temporarily stored JWT used when impersonating another user.
     * When the impersonated user is logged out, this value will be retrieved
     * and used to log in as the previous user.
     * @var string
     */
    storedJWT: '',

    /**
     * Temporary value that stores whether user is using the new UI
     */
    useNewUI: false,

    /**
     * Temporary value that stores whether user has been shown the new UI prompt.
     */
    shownNewUIPrompt: false,

    /**
     * Value to store if user is using mini side nav
     */
    useMiniSideNav: false
  },
  getters: {
    /**
     * Returns true if we have a fully populated customer context or an
     * interimCustomerContext
     *
     * @param {*} state the state object on this module
     * @return Boolean
     */
    hasCustomerContextId: state => {
      return (state.interimCustomerContext || state.customerContext.id) !== undefined
    },

    /**
     * Will return an integer representing the currently selected customer
     * context id (which may be the interimCustomerContext if we're in the
     * middle of switching)
     *
     * @param {*} state the state object on this module
     * @return Integer
     */
    customerContextId: state => {
      return state.interimCustomerContext || state.customerContext.id
    },

    /**
     * Returns true IFF we have a fully populated customer context
     *
     * @param {*} state the state object on this module
     * @return Boolean
     */
    hasCustomerContext: state => {
      return state.customerContext.id !== undefined
    },

    /**
     * Note that since we it is possible that the id property of the
     * CustomerRecord returned from this method may disagree with the value of
     * customerContextId from above. When looking for the id of the current
     * customer context it is always better the user the customerContextId
     * getter.
     *
     * @param {*} state the state object on this module
     * @return CustomerRecord
     */
    customerContext: state => {
      return state.customerContext
    },

    /**
     * @param {*} state the state object on this module
     * @return UserRecord
     */
    getUser: state => {
      // Eventually I want to use web sockets to push down updates but for now
      // we have a simple 1 day TTL
      const userCacheAge = Moment().diff(state.userCacheAge, 'seconds')

      if (userCacheAge >= 86400) {
        return ApiContainer.createRecord('User', {})
      }

      return state.user
    },

    keepLoggedIn: state => {
      return state.keepLoggedIn
    },

    hasReturnUrl: state => {
      return state.returnUrl !== ''
    },

    returnUrl: state => {
      return state.returnUrl
    },

    ignoreNextUserUpdate: state => {
      return state.ignoreNextUserUpdate
    },

    sessionExpirationTime: state => {
      return state.sessionExpirationTime
    },

    storedJWT: state => {
      return state.storedJWT
    },

    hasStoredJWT: state => {
      return state.storedJWT !== ''
    },

    useNewUI: state => {
      return state.useNewUI
    },
    shownNewUIPrompt: state => {
      return state.shownNewUIPrompt
    },

    useMiniSideNav: state => {
      return state.useMiniSideNav
    }
  },
  mutations: {
    /**
     * Updates the user stored in the state of this module and emits a websocket
     * update to keep the back-end informed.
     *
     * @param {*} state the state object on this module
     * @param UserRecord user
     */
    setUser (state, user) {
      const previousCustomers = state.user.customers || []
      state.user = user
      state.userCacheAge = Moment()
      // If statement here to support test automation (which does not have a
      // $socket client)
      if (this._vm.$socket) {
        this._vm.$socket.client.emit('set-user', user.id)
        // Keep the customer:<id> room subscriptions in sync with the user's
        // customer list so updates to any of them reach this client (VF-2565).
        unsubscribeFromRemovedCustomerRooms(this._vm.$socket, previousCustomers, user)
        subscribeToCustomerRooms(this._vm.$socket, user)
      }
    },

    /**
     * Updates the customer stored in the state of this module and emits a websocket
     * update to keep the back-end informed.
     *
     * @param {*} state the state object on this module
     * @param CutomerRecord customerContext
     */
    setCustomerContext (state, customerContext) {
      state.customerContext = customerContext
      // If statement here to support test automation (which does not have a
      // $socket client)
      if (this._vm.$socket) {
        this._vm.$socket.client.emit('set-customer-context', customerContext.id)
        // The server's set-customer-context handler leaves the previous
        // customer room; re-assert our list subscriptions so a previously
        // active customer that is still in the list keeps receiving updates
        // (VF-2565). Re-joining a room we are already in is a no-op server-side.
        subscribeToCustomerRooms(this._vm.$socket, state.user)
      }
    },

    /**
     *
     *
     * @param {*} state the state object on this module
     * @param Integer customerContextId id of the newly selected customer
     */
    setInterimCustomerContext (state, customerContextId) {
      state.interimCustomerContext = customerContextId
    },

    /**
     * Set whether the user should stay logged in across sessions
     * @param state
     * @param value
     */
    setKeepLoggedIn (state, value) {
      state.keepLoggedIn = value
    },

    /**
     * Set the return url
     * @param state
     * @param returnUrl
     */
    setReturnUrl (state, returnUrl) {
      state.returnUrl = returnUrl
    },

    setIgnoreNextUserUpdate (state, value) {
      state.ignoreNextUserUpdate = value
    },

    setSessionExpirationTime (state, value) {
      state.sessionExpirationTime = value
    },

    /**
     * Set the stored JWT (stored when impersonating another user)
     * @param state
     * @param value
     */
    setStoredJWT (state, value) {
      state.storedJWT = value
    },

    setUseNewUI (state, value) {
      state.useNewUI = value
    },

    setShownNewUIPrompt (state, value) {
      state.shownNewUIPrompt = value
    },

    setUseMiniSideNav (state, value) {
      state.useMiniSideNav = value
    }
  },
  actions: {

    ignoreNextUserUpdate (context, message) {
      context.commit('setIgnoreNextUserUpdate', true)
    },

    /**
     * Called by vue-socket.io anytime a user-updated event is emitted to this
     * client. See more at https://www.npmjs.com/package/vue-socket.io-extended
     * for details of how events trigger actions in vuex stores.
     *
     * @param {Object} context vuex state container
     * @param {*} message the message included with the web socket event (not
     * used at this time)
     * @return {undefined}
     */
    socket_userUpdated (context, message) {
      throttledUpdateUser(context, message)
    },

    /**
     * Called by vue-socket.io anytime a user-settings-updated event is emitted
     * to this client.
     *
     * @param {Object} context vuex state container
     * @param {*} message the message included with the web socket event (not
     * used at this time)
     * @return {undefined}
     */
    socket_userSettingsUpdated (context, message) {
      throttledUpdateUser(context, message)
      // Per-user feature flags (user tier of the getFeatureFlag cascade) may
      // have changed — refresh the global feature blob too (VF-4173).
      context.dispatch('featureModule/fetchFeatures', null, { root: true })
    },

    /**
     * Called by vue-socket.io anytime a user-roles-updated event is emitted
     * to this client.
     *
     * @param {Object} context vuex state container
     * @param {*} message the message included with the web socket event (not
     * used at this time)
     * @return {undefined}
     */
    socket_userRolesUpdated (context, message) {
      throttledUpdateUser(context, message)
    },

    /**
     * Called by vue-socket.io anytime a role-permissions-updated event is
     * emitted to this client.
     *
     * @param {Object} context vuex state container
     * @param {*} message the message included with the web socket event (not
     * used at this time)
     * @return {undefined}
     */
    socket_rolePermissionsUpdated (context, message) {
      throttledUpdateUser(context, message)
    },

    /**
     * Called by vue-socket.io anytime a customer-updated event is emitted to
     * this client.
     *
     * @param Object context vuex state container
     * @param {*} message the message included with the web socket event (not
     * used at this time)
     * @return undefined
     */
    socket_customerUpdated (context, message) {
      throttledUpdateCustomer(context, message)
      // A customer in the user's list changed (e.g. renamed or moved in the
      // hierarchy); refresh the cached customer list too, not just the active
      // context (VF-2565).
      throttledUpdateUser(context, message)
    },

    /**
     * Called by vue-socket.io anytime a customer-settings-updated event is
     * emitted to this client.
     *
     * @param {Object} context vuex state container
     * @param {*} message the message included with the web socket event (not
     * used at this time)
     * @return {undefined}
     */
    socket_customerSettingsUpdated (context, message) {
      throttledUpdateCustomer(context, message)
      // Per-customer feature flags (customer tier of the getFeatureFlag
      // cascade) may have changed — refresh the global feature blob (VF-4173).
      context.dispatch('featureModule/fetchFeatures', null, { root: true })
    },

    /**
     * Called by vue-socket.io anytime an application-release event is emitted
     * to this client.
     *
     * @param Object context vuex state container
     * @param {*} message the message included with the web socket event (not
     * used at this time)
     * @return undefined
     */
    socket_applicationRelease (context, message) {
      if (message.version !== context.rootGetters['appMetadataModule/version']) {
        throttledUpdateUser(context, message)
        throttledUpdateCustomer(context, message)
        throttledUpdateLocale(context, message)
        context.dispatch('featureModule/fetchFeatures', null, { root: true })
        context.dispatch('globalMessagingModule/showSuccessFlashBanner', i18n.t('application_refresh_message', { version: message.version }), { root: true })
      }
    },

    /**
     * Called by vue-socket.io anytime an application-release-quiet event is
     * emitted to this client. Does the same thing internally as
     * socket_applicationRelease() above but without the banner message
     * prompting the user to refresh the page
     *
     * @param Object context vuex state container
     * @param {*} message the message included with the web socket event (not
     * used at this time)
     * @return undefined
     */
    socket_applicationReleaseQuiet (context, message) {
      if (message.version !== context.rootGetters['appMetadataModule/version']) {
        throttledUpdateUser(context, message)
        throttledUpdateCustomer(context, message)
        throttledUpdateLocale(context, message)
        context.dispatch('featureModule/fetchFeatures', null, { root: true })
      }
    },

    /**
     * Called by vue-socket.io anytime the update-maintenance-mode event is
     * emitted to this client.
     *
     * @param Object context vuex state container
     * @param {*} message the message included with the web socket event (not
     * used at this time)
     * @return undefined
     */
    socket_updateMaintenanceMode (context, message) {
      updateMaintenanceMode(context, message)
    },

    /**
     *
     * @param Object context vuex state container
     * @param UserRecord user
     */
    setUser (context, user) {
      context.commit('setUser', user)
    },

    /**
     * Fires a call to the API to fetch a full customer object and updates local
     * application state with the new customer.
     *
     * @param Object context vuex state container
     * @param Integer customerId
     */
    changeCustomerContext (context, customerId) {
      // Set an interim customer context id in order to allow the following API
      // calls to pass up the customer newly set context header before the call
      // below (with the rest of the customer info returns).
      context.commit('setInterimCustomerContext', customerId)

      ApiContainer.getMapper('Customer')
        .find('self', { params: { expand: 'customer_settings' } })
        .then((customer) => {
          context.commit('setCustomerContext', customer)
          // Once we have the customer returned from the API we no longer have a
          // need for the interim customer context
          context.commit('setInterimCustomerContext', null)
          // Feature flags are resolved against the ACTIVE customer, so re-fetch
          // them for the newly selected customer context (VF-4173).
          context.dispatch('featureModule/fetchFeatures', null, { root: true })
        })
        .catch(error => {
          if (error) {
            context.dispatch('globalMessagingModule/showErrorBanner', 'There was an issue loading the page. Please try again or contact support.')
            throw (error)
          }
        })
    },

    /**
     * Wipes the user object on this modules state to it's original value (a
     * blank UserRecord object)
     *
     * @param Object context vuex state container
     * @return undefined
     */
    resetUser (context) {
      const user = ApiContainer.createRecord('User', {})
      context.commit('setUser', user)
    },

    /**
     * Wipes the customer context object on this modules state to it's original
     * value (a blank CustomerRecord object)
     *
     * @param Object context vuex state container
     * @return undefined
     */
    resetCustomerContext (context) {
      const customer = ApiContainer.createRecord('Customer', {})
      context.commit('setCustomerContext', customer)
    },

    /**
     * Set the return url
     * @param context
     * @param returnUrl
     */
    setReturnUrl (context, returnUrl) {
      context.commit('setReturnUrl', returnUrl)
    },

    /**
     * Clear the return url
     * @param context
     * @param returnUrl
     */
    clearReturnUrl (context, returnUrl) {
      context.commit('setReturnUrl', '')
    },

    /**
     * Set the time the session will expire
     * @param context
     * @param expirationTime, unixtime
     */
    setSessionExpirationTime (context, expirationTime) {
      context.commit('setSessionExpirationTime', expirationTime)
    },

    /**
     * Set the stored JWT (used when impersonating another user)
     * @param context
     * @param jwt
     */
    setStoredJWT (context, jwt) {
      context.commit('setStoredJWT', jwt)
    },

    setUseNewUI (context, value) {
      context.commit('setUseNewUI', value)
    }
  }
}
